import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import BreadcrumbDates, { type Breadcrumb } from "../../../components/core/blocks/BreadcrumbDates";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../../../utils/dashboardLinks";
import { useGetV4ProductReportByProduct } from "../api/product/v4ProductReportByProduct";

export function ProductBreadcrumbs({
  categoryId,
  productId,
  categoryDetails,
  categoryNavData,
  dashboardName,
  dashboardHref,
  getProductHref,
  hideDateSelector,
}: {
  categoryId?: string;
  productId?: string;
  categoryDetails?: { leaf_category_name?: string; href?: string };
  categoryNavData?: { name: string; href?: string; altDisplayMarkup?: React.ReactNode }[];
  dashboardName: string;
  dashboardHref: string;
  getProductHref: (productId: number) => string;
  hideDateSelector?: boolean;
}) {
  const { t } = useTranslation(["perf_reports"]);
  const byProductOptionsGetter = useGetV4ProductReportByProduct(categoryId);
  const categoryProductsQuery = useQuery({
    ...byProductOptionsGetter({
      pagination: { pageIndex: 0, pageSize: 200 },
      sorting: [],
    }),
    enabled: Boolean(categoryId),
  });

  const categoryProducts = useMemo(() => {
    if (!categoryProductsQuery.data) return [];
    return categoryProductsQuery.data.data.map((product) => ({
      ...product,
      id: product.product_id,
      name: `${product.product_title} ${product.asin} (${product.sku})`,
      altDisplayMarkup: (
        <div>
          <span
            className={css({
              maxWidth: "100%",
              lineClamp: 1,
              fontWeight: "500",
              fontSize: "1.5rem",
            })}
          >
            {product.product_title}
          </span>
          <i
            className={css({
              display: "block",
              fontWeight: "light",
              color: "#2e0054",
              fontSize: "1rem",
            })}
          >
            {product.asin} ({product.sku})
          </i>
        </div>
      ),
      href: getProductHref(product.product_id),
      isLoading: categoryProductsQuery.isLoading,
    }));
  }, [categoryProductsQuery.data, categoryProductsQuery.isLoading, getProductHref]);

  const currentProduct = useMemo(() => {
    if (!productId) return undefined;
    return categoryProducts.find((p) => p.product_id === parseInt(productId));
  }, [categoryProducts, productId]);

  const breadCrumbItems = useMemo<Breadcrumb[]>(
    () => [
      AGENCY_DASHBOARD_BREADCRUMB_SPEC,
      {
        name: dashboardName,
        href: dashboardHref,
        dropdownLinkOptions: DASHBOARD_LINKS,
        searchable: false,
        unsorted: true,
        dropdownSubtitle: t(($) => $.perf_reports["Dashboards"]),
      },
      {
        name: categoryDetails?.leaf_category_name ?? "",
        href: categoryDetails?.href,
        dropdownLinkOptions: categoryNavData,
        searchable: true,
        searchPlaceholderText: t(($) => $.perf_reports["Search categories..."]),
        dropdownSubtitle: t(($) => $.perf_reports["Categories"]),
      },
      {
        name: currentProduct?.name ?? "",
        href: currentProduct?.href,
        dropdownLinkOptions: categoryProducts,
        searchable: true,
        searchPlaceholderText: t(($) => $.perf_reports["Search products..."]),
        dropdownSubtitle: t(($) => $.perf_reports["Products"]),
      },
    ],
    [categoryDetails, categoryNavData, categoryProducts, currentProduct, dashboardName, dashboardHref, t],
  );

  return <BreadcrumbDates items={breadCrumbItems} hideDateSelector={hideDateSelector} />;
}
